import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useMyLeaveBalances } from '@/hooks/leave/useLeaveBalances';
import { Calculator, Calendar, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';
import { format, addDays, differenceInDays, parseISO } from 'date-fns';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

interface ProjectionScenario {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number;
}

interface BalanceProjectionProps {
  className?: string;
}

export function BalanceProjection({ className = "" }: BalanceProjectionProps) {
  const { data: balances } = useMyLeaveBalances();
  const [scenarios, setScenarios] = useState<ProjectionScenario[]>([]);
  const [newScenario, setNewScenario] = useState({
    leaveType: '',
    startDate: '',
    endDate: ''
  });

  const leaveTypes = useMemo(() => {
    if (!balances) return [];
    return balances.map(balance => ({
      value: balance.policy.leave_type,
      label: getLeaveTypeLabel(balance.policy.leave_type)
    }));
  }, [balances]);

  const calculateDays = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    return differenceInDays(endDate, startDate) + 1;
  };

  const projectedBalances = useMemo(() => {
    if (!balances) return [];
    
    return balances.map(balance => {
      const scenarioUsage = scenarios
        .filter(scenario => scenario.leaveType === balance.policy.leave_type)
        .reduce((total, scenario) => total + scenario.days, 0);
      
      const projectedAvailable = balance.current_balance_days - scenarioUsage;
      
      return {
        ...balance,
        scenarioUsage,
        projectedAvailable,
        isOverdrawn: projectedAvailable < 0
      };
    });
  }, [balances, scenarios]);

  const addScenario = () => {
    if (!newScenario.leaveType || !newScenario.startDate || !newScenario.endDate) return;
    
    const days = calculateDays(newScenario.startDate, newScenario.endDate);
    const scenario: ProjectionScenario = {
      id: Date.now().toString(),
      leaveType: newScenario.leaveType,
      startDate: newScenario.startDate,
      endDate: newScenario.endDate,
      days
    };
    
    setScenarios([...scenarios, scenario]);
    setNewScenario({ leaveType: '', startDate: '', endDate: '' });
  };

  const removeScenario = (id: string) => {
    setScenarios(scenarios.filter(s => s.id !== id));
  };

  const clearAllScenarios = () => {
    setScenarios([]);
  };

  if (!balances) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Balance Projection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New Scenario */}
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Plan Future Leave
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select 
                value={newScenario.leaveType} 
                onValueChange={(value) => setNewScenario(prev => ({ ...prev, leaveType: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={newScenario.startDate}
                onChange={(e) => setNewScenario(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={newScenario.endDate}
                onChange={(e) => setNewScenario(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
            
            <div className="flex items-end">
              <Button onClick={addScenario} className="w-full">
                Add Scenario
              </Button>
            </div>
          </div>

          {newScenario.startDate && newScenario.endDate && (
            <div className="text-sm text-muted-foreground">
              Duration: {calculateDays(newScenario.startDate, newScenario.endDate)} day(s)
            </div>
          )}
        </div>

        {/* Current Scenarios */}
        {scenarios.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Planned Leave Scenarios</h4>
                <Button variant="outline" size="sm" onClick={clearAllScenarios}>
                  Clear All
                </Button>
              </div>
              
              {scenarios.map((scenario) => (
                <div key={scenario.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">
                        {scenario.leaveType}
                      </Badge>
                      <span className="font-medium">{scenario.days} days</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {format(parseISO(scenario.startDate), 'MMM d')} - {format(parseISO(scenario.endDate), 'MMM d')}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeScenario(scenario.id)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Projected Balances */}
        <Separator />
        <div className="space-y-4">
          <h4 className="font-medium">Projected Impact</h4>
          
          {projectedBalances.map((balance) => (
            <div key={balance.policy.leave_type} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h5 className="font-medium capitalize flex items-center gap-2">
                    {getLeaveTypeLabel(balance.policy.leave_type)}
                    {balance.isOverdrawn && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Overdrawn
                      </Badge>
                    )}
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    Current: {balance.current_balance_days} days
                    {balance.scenarioUsage > 0 && (
                      <span className="ml-2">
                        • Planned: -{balance.scenarioUsage} days
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-xl font-bold flex items-center gap-1 ${
                    balance.isOverdrawn ? 'text-destructive' : 'text-foreground'
                  }`}>
                    {balance.projectedAvailable < balance.current_balance_days ? (
                      <TrendingDown className="h-4 w-4" />
                    ) : (
                      <TrendingUp className="h-4 w-4" />
                    )}
                    {balance.projectedAvailable}
                  </div>
                  <div className="text-xs text-muted-foreground">days remaining</div>
                </div>
              </div>

              {balance.isOverdrawn && (
                <div className="p-2 bg-destructive/10 border border-destructive/20 rounded text-sm">
                  <p className="text-destructive">
                    ⚠️ This scenario would overdraw your balance by {Math.abs(balance.projectedAvailable)} days
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}